import { encodeDate, unpackToDate } from '@campfire/hot-date';
import { uniq } from 'lodash';
import { Box, Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { DateTime } from 'luxon';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCard, getAlertCardColors } from '../../../../../../common/cards/alert-card/AlertCard';
import { useUser } from '../../../../../../global/auth/useUser';
import { useCampfireQuery } from '../../../../../../global/network/useCampfireQuery';
import {
  GET_MY_ELEMENTS_SIDEBAR_INCOMPLETE_REPORTS_COUNT,
  GET_MY_ELEMENTS_SIDEBAR_INCOMPLETE_REPORTS_DATA,
} from './side-bar-incomplete-reports-model.gql';
import {
  GetMyElementsSideBarIncompleteReportsData,
  GetMyElementsSideBarIncompleteReportsDataVariables,
} from './__generated__/GetMyElementsSideBarIncompleteReportsData';
import {
  GetMyElementsSideBarIncompleteReportsCount,
  GetMyElementsSideBarIncompleteReportsCountVariables,
} from './__generated__/GetMyElementsSideBarIncompleteReportsCount';
import { MyActivityCheckOutDialog } from '../../main-section/my-activities/CICO/MyActivityCheckOutDialog';
import { RosteredStatus } from '../../../../activities-v2/ActivityTypes';

export const SideBarIncompleteReports = () => {
  const {
    getVolunteerIdentity,
    user: { userIdentityService, configItems },
  } = useUser();
  const volunteerIdentity = getVolunteerIdentity();

  if (
    configItems.vm.preventVolunteerReports &&
    !userIdentityService.isActivityLeader &&
    !userIdentityService.isProgramManager &&
    !userIdentityService.isVmAdmin
  ) {
    return null;
  }

  const { darkTextColor, lightTextColor } = getAlertCardColors('urgent');

  const { data, loading, refetch } = useCampfireQuery<
    GetMyElementsSideBarIncompleteReportsData,
    GetMyElementsSideBarIncompleteReportsDataVariables
  >(GET_MY_ELEMENTS_SIDEBAR_INCOMPLETE_REPORTS_DATA, {
    options: {
      variables: {
        volunteerId: volunteerIdentity.volunteerId,
      },
    },
  });

  const { ledActivityIds, rosteredDates } = useMemo(() => {
    const isActiveProgram = (program: { dateDeleted: any; dateSuspended: any }) =>
      !program.dateDeleted && !program.dateSuspended;

    const led = new Set(
      data?.vm.volunteer?.activityEnrolments
        .filter((ae) => isActiveProgram(ae.activity.program))
        .map((ae) => ae.activity.activityId)
        .filter((activityId) => userIdentityService.isLeaderOfActivity(activityId)) ?? []
    );

    const rostered = new Map<string, Set<string>>();
    const activeRosterings = (data?.vm.volunteer?.rosterings ?? []).filter((r) =>
      isActiveProgram(r.publishedRoster.activity.program)
    );
    activeRosterings.forEach((r) => {
      const { activityId } = r.publishedRoster.activity;
      const dates = rostered.get(activityId) ?? new Set<string>();
      dates.add(r.publishedRoster.activityDate);
      rostered.set(activityId, dates);
    });

    return { ledActivityIds: led, rosteredDates: rostered };
  }, [data]);

  const activityIds = useMemo(() => uniq([...Array.from(ledActivityIds), ...Array.from(rosteredDates.keys())]), [
    ledActivityIds,
    rosteredDates,
  ]);

  const { data: countData, loading: countLoading, refetch: refetchCount } = useCampfireQuery<
    GetMyElementsSideBarIncompleteReportsCount,
    GetMyElementsSideBarIncompleteReportsCountVariables
  >(GET_MY_ELEMENTS_SIDEBAR_INCOMPLETE_REPORTS_COUNT, {
    options: {
      skip: activityIds.length === 0,
      variables: {
        activityIds,
        from: encodeDate(DateTime.local().minus({ weeks: 2 })),
        until: encodeDate(DateTime.local()),
      },
    },
  });

  const incompleteReportsNum = useMemo(
    () =>
      countData?.vm.incompleteActivityReports.reduce((total, activity) => {
        const occurrences = uniq(activity.occurrences);
        if (ledActivityIds.has(activity.activityId)) return total + occurrences.length;
        const dates = rosteredDates.get(activity.activityId);
        return total + occurrences.filter((occurrence) => dates?.has(occurrence)).length;
      }, 0) ?? 0,
    [countData, ledActivityIds, rosteredDates]
  );

  const cicoConflict =
    data?.vm.volunteer?.isCheckedIn &&
    unpackToDate(data.vm.volunteer.checkedIn?.sessionReport.activityReport.activityDate) < DateTime.local();
  const [cicoOpen, setCICOOpen] = useState<boolean>();

  const cicoActivity = data?.vm.volunteer?.checkedIn?.sessionReport.activityReport.activity;
  const tempStatus: RosteredStatus = 'rostered';

  const formattedCICOActivity = useMemo(() => {
    return (
      cicoActivity && {
        ...cicoActivity,
        activityDate: data?.vm.volunteer?.checkedIn?.sessionReport.activityReport.activityDate,
        status: tempStatus,
        isEnrollable: true,
      }
    );
  }, [cicoActivity]);

  const handleSuccessCheckout = () => {
    if (refetch) refetch();
    if (refetchCount) refetchCount();
  };

  return (
    <>
      {cicoOpen && formattedCICOActivity !== undefined ? (
        <MyActivityCheckOutDialog
          open={cicoOpen}
          type={'myself'}
          onClose={() => setCICOOpen(false)}
          activity={formattedCICOActivity}
          handleSuccess={handleSuccessCheckout}
        />
      ) : null}
      {loading || countLoading ? (
        <Skeleton variant='text' width='100%' height={105} style={{ paddingBottom: '24px' }} />
      ) : cicoConflict ? (
        <Box paddingBottom={3} width='100%' onClick={() => setCICOOpen(true)}>
          <AlertCard title='Outstanding Check Out' variant='urgent'>
            <Typography variant='subtitle2' style={{ fontSize: '14px', fontWeight: 400, color: lightTextColor }}>
              You have an outstanding check-out. <br />
              You will be unable to check-in to another session until you&apos;ve checked out.
            </Typography>

            <Typography
              variant='body2'
              display='inline'
              style={{ textDecoration: 'underline', color: darkTextColor, fontWeight: 700 }}
            >
              Click here
            </Typography>
            <Typography
              variant='subtitle2'
              display='inline'
              style={{ fontSize: '14px', fontWeight: 400, color: lightTextColor }}
            >
              {' to complete.'}
            </Typography>
          </AlertCard>
        </Box>
      ) : incompleteReportsNum > 0 ? (
        <Link
          to={{
            pathname: '/volunteering/activity-report',
            search: '?&tab=incomplete',
          }}
          style={{ textDecoration: 'none' }}
        >
          <Box paddingBottom={3} width='100%'>
            <AlertCard title='Outstanding Activity Report' variant='urgent'>
              <Typography variant='subtitle2' style={{ fontSize: '14px', fontWeight: 400, color: lightTextColor }}>
                You have <span style={{ fontWeight: 'bold', color: darkTextColor }}>{incompleteReportsNum}</span>{' '}
                outstanding reports.
              </Typography>

              <Typography
                variant='body2'
                display='inline'
                style={{ textDecoration: 'underline', color: darkTextColor, fontWeight: 700 }}
              >
                Click here
              </Typography>
              <Typography
                variant='subtitle2'
                display='inline'
                style={{ fontSize: '14px', fontWeight: 400, color: lightTextColor }}
              >
                {' to complete.'}
              </Typography>
            </AlertCard>
          </Box>
        </Link>
      ) : null}
    </>
  );
};
